import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../Contexts/AuthContext';

type AuthenticatedImage = {
  url: string | null;
  loading: boolean;
};

export const useAuthenticatedImage = (
  url?: string | null,
  organizationId?: string,
  refreshKey?: unknown,
): AuthenticatedImage => {
  const { getAccessTokenSilently } = useAuth();
  const { organizationId: routeOrganizationId } = useParams();
  const orgId = organizationId ?? routeOrganizationId;
  const [image, setImage] = useState<AuthenticatedImage>({ url: null, loading: Boolean(url) });

  useEffect(() => {
    if (!url) {
      setImage({ url: null, loading: false });
      return undefined;
    }

    const controller = new AbortController();
    let objectUrl: string | null = null;

    const load = async () => {
      try {
        const token = await getAccessTokenSilently();
        const headers: Record<string, string> = { Authorization: `Bearer ${token}` };
        if (orgId) headers['X-Organization-Key'] = orgId;

        const response = await fetch(url, {
          headers,
          signal: controller.signal,
          cache: 'no-cache',
        });
        if (!response.ok) throw new Error(`Image request failed with ${response.status}`);

        const blob = await response.blob();
        if (controller.signal.aborted) return;

        objectUrl = URL.createObjectURL(blob);
        setImage({ url: objectUrl, loading: false });
      } catch (error) {
        if (controller.signal.aborted) return;
        console.error('Failed to load image:', error);
        setImage({ url: null, loading: false });
      }
    };

    setImage({ url: null, loading: true });
    load();

    return () => {
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url, orgId, getAccessTokenSilently, refreshKey]);

  return image;
};

export default useAuthenticatedImage;
